import { isOfflineMode } from "@/helpers/offlineMode";
import {
  AllocationSkillResponse,
  AllocationsUploadResponse,
  AvailableSkill,
  ShiftCapsPayload,
  ShiftCapsSubmitResponse,
  UpdateUserSkillsPayload,
  UserSkillAssignment,
  ValidationErrorResponse,
  WorkerAllocation,
} from "@/types/allocations";
import apiClient, { getPhoenixToken } from "./apiClient";
import { mockAvailableSkills, mockShiftCaps, mockUserSkillAssignments } from "./mockData";

const ALLOCATIONS_BASE_PATH = "/allocations";

const getAllocations = async (abortSignal?: AbortSignal): Promise<WorkerAllocation[]> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return [];
  }

  const response = await apiClient.get(ALLOCATIONS_BASE_PATH, {
    signal: abortSignal,
  });
  return response.data as WorkerAllocation[];
};

const saveAllocations = async (allocations: WorkerAllocation[]): Promise<void> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return;
  }

  await apiClient.put(ALLOCATIONS_BASE_PATH, allocations);
};

const uploadSpreadsheet = async (file: File): Promise<AllocationsUploadResponse> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return {
      success: true,
      message: "Spreadsheet uploaded successfully",
      processedCount: 8,
      failedCount: 0,
    };
  }

  const formData = new FormData();
  formData.append("file", file);

  const response = await apiClient.post(`${ALLOCATIONS_BASE_PATH}/upload`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data as AllocationsUploadResponse;
};

const downloadTemplate = async (shiftDate: string): Promise<Blob> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return new Blob(["userId,receivingLeads,shiftStart,shiftEnd,19,20,21,42,43,49,53"], {
      type: "text/csv",
    });
  }

  const response = await apiClient.get(`${ALLOCATIONS_BASE_PATH}/allocation-skills/download-template`, {
    params: {
      shiftDate,
    },
    responseType: "blob",
  });
  return response.data as Blob;
};

const validateTemplate = async (file: File, shiftDate: string): Promise<AllocationSkillResponse[]> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return [mockShiftCaps[0]];
  }

  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await apiClient.post(`${ALLOCATIONS_BASE_PATH}/allocation-skills/validate-template`, formData, {
      params: {
        shiftDate,
      },
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data as AllocationSkillResponse[];
  } catch (error: any) {
    if (error.response?.status === 400) {
      const validationErrors = error.response.data as ValidationErrorResponse;
      throw {
        type: "validation",
        errors: validationErrors,
      };
    }
    throw error;
  }
};

const submitShiftCaps = async (
  shiftCapsData: ShiftCapsPayload[],
  shiftDate: string
): Promise<ShiftCapsSubmitResponse> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      message: `Successfully processed agent shift cap records.`,
    };
  }

  try {
    const response = await apiClient.post(`${ALLOCATIONS_BASE_PATH}/allocation-skills/shiftcaps`, shiftCapsData, {
      params: {
        shiftDate,
      },
    });
    return response.data as ShiftCapsSubmitResponse;
  } catch (error: any) {
    if (error.response?.status === 400) {
      const validationErrors = error.response.data as ValidationErrorResponse;
      throw { type: "validation", errors: validationErrors };
    }
    throw error;
  }
};

const toggleReceivingLeads = async (userId: number, isReceivingLeads: boolean): Promise<void> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return;
  }

  await apiClient.post(`${ALLOCATIONS_BASE_PATH}/allocation-skills/receiving-leads`, {
    userId,
    isReceivingLeads,
  });
};

const getShiftCaps = async (shiftDate: string, abortSignal?: AbortSignal): Promise<AllocationSkillResponse[]> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockShiftCaps;
  }

  const response = await apiClient.get(`${ALLOCATIONS_BASE_PATH}/allocation-skills/shiftcaps`, {
    params: {
      shiftDate,
    },
    signal: abortSignal,
  });
  return response.data as AllocationSkillResponse[];
};

const isReady = (): boolean => {
  if (isOfflineMode()) {
    return true;
  }
  return !!getPhoenixToken();
};

const getAvailableSkills = async (): Promise<AvailableSkill[]> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockAvailableSkills;
  }

  const response = await apiClient.get(`${ALLOCATIONS_BASE_PATH}/allocation-skills/`);
  return response.data as AvailableSkill[];
};

const getUserSkillAssignments = async (): Promise<UserSkillAssignment[]> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockUserSkillAssignments;
  }

  const response = await apiClient.get(`${ALLOCATIONS_BASE_PATH}/allocation-skills/users/`);
  return response.data as UserSkillAssignment[];
};

const updateUserSkills = async (payload: UpdateUserSkillsPayload): Promise<void> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return;
  }

  await apiClient.post(`${ALLOCATIONS_BASE_PATH}/allocation-skills/`, payload);
};

export const allocationsService = {
  getAllocations,
  saveAllocations,
  uploadSpreadsheet,
  downloadTemplate,
  validateTemplate,
  submitShiftCaps,
  getShiftCaps,
  toggleReceivingLeads,
  getAvailableSkills,
  getUserSkillAssignments,
  updateUserSkills,
  isReady,
};
